import React, { useState, useEffect } from "react"
import { getCurrentBookings } from "../../api/apiClient"
import { AlertTriangle, Clock, Eye } from "lucide-react"
import { useNavigate } from "react-router-dom"

const UserBookings = () => {
  const [bookings, setBookings] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const navigate = useNavigate()

  // Fetch booking data on component mount
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setIsLoading(true)
        setError(null)
        // API call is secured by JWT, which apiClient handles automatically
        const data = await getCurrentBookings()
        setBookings(data)
      } catch (err) {
        // Handle unauthorized errors, etc.
        // We log the error but show a user-friendly message
        setError(
          err.message ||
            "Failed to load bookings. Please check your connection."
        )
        setBookings([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchBookings()
  }, []) // Empty dependency array ensures this runs only once on mount

  const renderStatusBadge = status => {
    const statusClasses = {
      Confirmed: "bg-green-100 text-green-800",
      Pending: "bg-yellow-100 text-yellow-800",
      Cancelled: "bg-red-100 text-red-800",
    }
    return (
      <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
          statusClasses[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    )
  }

  // --- Rendering States ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 bg-white rounded-xl shadow-lg">
        <Clock className="w-6 h-6 animate-spin text-indigo-600 mr-3" />
        <p className="text-gray-600">Loading current bookings...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg">
        <AlertTriangle className="w-5 h-5 text-red-600 inline mr-2" />
        <p className="text-red-700 font-medium">Error: {error}</p>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <h3 className="text-xl font-semibold mb-4 text-gray-700">
        Current & Upcoming Bookings ({bookings.length})
      </h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Room
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                  No current or upcoming bookings found.
                </td>
              </tr>
            ) : (
              bookings.map(booking => (
                <tr key={booking.bookingID}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {/* Display start date, and end date if recurring */}
                    {new Date(booking.startDate).toLocaleDateString()}
                    {booking.bookingType === "Recurring" &&
                      ` - ${new Date(booking.endDate).toLocaleDateString()}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {booking.startTime} - {booking.endTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {booking.classroomName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {booking.bookingType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {renderStatusBadge(booking.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      className="text-indigo-600 hover:text-indigo-900 flex items-center transition duration-150"
                      // Use navigate to go to the new details route
                      onClick={() => navigate(`/bookings/${booking.bookingID}`)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* <button className="mt-6 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition duration-150 shadow-md flex items-center">
        <Plus className="w-5 h-5 mr-1" />
        Book New Room
      </button> */}
    </div>
  )
}

export default UserBookings
